import { useState, useEffect, useRef, useCallback, useMemo, memo } from "react";
import sanityClient from "../../sanityClient";

// Separate query constant
const BANNER_QUERY = `{
  "homeBannerContent": *[_type == "homePageContent"][0].homeBannerText{ 
    heading 
  },
  "bannerData": *[_type == "bannerImageSection" && page == "home"][0]{
    bannerType,
    "imageUrl": bannerImage.asset->url,
    bannerVideoUrl,
    altText
  }
}`;

const HomeBanner = () => {
  const [bannerState, setBannerState] = useState({
    data: null,
    loading: true,
    error: null
  });
  const [isPlaying, setIsPlaying] = useState(false);
  
  const videoRef = useRef(null);
  const imageRef = useRef(null);
  const controllerRef = useRef(null);

  // Memoized video URL parameters
  const videoUrlParams = useMemo(() => 
    new URLSearchParams({
      autoplay: '1',
      mute: '1',
      modestbranding: '1',
      rel: '0'
    }).toString(),
  []);

  // Optimized data fetching
  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        // Abort previous requests
        if (controllerRef.current) {
          controllerRef.current.abort();
        }
        controllerRef.current = new AbortController();

        const result = await sanityClient.fetch(BANNER_QUERY, {}, {
          signal: controllerRef.current.signal
        });

        setBannerState({
          data: result,
          loading: false,
          error: null
        });
      } catch (error) {
        if (error.name === 'AbortError') return;
        
        setBannerState(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to load banner content'
        }));
        console.error("Error fetching banner data:", error);
      }
    };

    fetchBannerData();

    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  // Optimized play/pause handler
  const handlePlayPause = useCallback(() => {
    const { data } = bannerState;
    if (!data?.bannerData) return;

    const isYouTube = data.bannerData.bannerType === "video" &&  data.bannerData.bannerVideoUrl.includes("youtube");

    if (isYouTube) {
      setIsPlaying(prev => !prev);
    } else if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(error => {
          console.error("Video playback failed:", error);
        });
      }
      setIsPlaying(prev => !prev);
    }
  }, [bannerState.data, isPlaying]);

  // Image loading optimization
  useEffect(() => {
    if (imageRef.current && 'loading' in HTMLImageElement.prototype) {
      imageRef.current.loading = 'eager';
      
      // Preload video if available
      const { data } = bannerState;
      if (data?.bannerData?.bannerVideoUrl && !data.bannerData.bannerVideoUrl.includes("youtube")) {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'video';
        link.href = data.bannerData.bannerVideoUrl;
        document.head.appendChild(link);
      }
    }
  }, [bannerState.data]);

  // if (bannerState.loading) {
  //   return <div className="banner-section banner-loading" aria-busy="true">Loading...</div>;
  // }

  if (bannerState.error) {
    return <div className="banner-section banner-error" role="alert">{bannerState.error}</div>;
  }

  const { data } = bannerState;
  const isYouTubeVideo = data?.bannerData?.bannerType === "video" &&  data.bannerData.bannerVideoUrl?.includes("youtube");

  return (
    <section className="banner-section">
      <div className="homebanner-detail">
        {data?.bannerData && (
          <>
            {!isPlaying ? (
              <img ref={imageRef} src="assets/home/banner-1.webp" alt={data.bannerData.altText || "Banner"} className="img-responsive" decoding="async" fetchpriority="high" width="100%" height="auto" onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'assets/fallback-banner.webp';
                }}/>
            ) : 
            isYouTubeVideo ? (
              <iframe className="img-responsive" src={`${data.bannerData.bannerVideoUrl}?${videoUrlParams}`} title="YouTube Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen loading="lazy"/>
            ) : 
            (
              <video className="img-responsive" src={data.bannerData.bannerVideoUrl} ref={videoRef} muted loop playsInline onError={(e) => console.error("Video loading error:", e)}/>
            )}
          </>
        )}

        <div>
          <h1 className="heading">{data?.homeBannerContent?.heading}</h1>
          {/* <Link className="play-butn" onClick={handlePlayPause} aria-label={isPlaying ? "Pause video" : "Play video"} role="button"> <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} aria-hidden="true"/></Link> */}
        </div>
      </div>
    </section>
  );
};

// HomeBanner.displayName = 'HomeBanner';

export default memo(HomeBanner);